import React, { useEffect, useMemo, useState, useCallback } from 'react'
import ReactDOM from 'react-dom'
import './Clockify.css'
import TeamHeader from '../TeamHeader/TeamHeader'

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
    PiMagnifyingGlass,
    PiUsers,
    PiCheckCircleFill,
    PiXCircleFill,
    PiUserCircle,
    PiCaretDown,
    PiCaretRight,
    PiClipboardText,
    PiCrown,
    PiCircleDashed,
    PiSpinnerGap,
    PiMicrosoftTeamsLogo,
    PiUsersThree,
    PiPackage,
    PiGraduationCap,
    PiListChecks,
    PiFileText,
    PiGift,
    PiEnvelopeSimple,
    PiIdentificationCard,
    PiPresentation,
    PiBuildings,
    PiClock,
    PiBell,
    PiX,
} from "react-icons/pi";
import type { IconType } from "react-icons";

/* ── Portal tooltip badge for tasks ────────────────────────── */

interface TooltipState {
    text: string;
    x: number;
    y: number;
}

const TaskBadge = ({
    status,
    tooltip,
    children,
}: {
    status: string;
    tooltip: string;
    children: React.ReactNode;
}) => {
    const [tip, setTip] = useState<TooltipState | null>(null);

    const handleMouseEnter = useCallback((e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setTip({
            text: tooltip,
            x: rect.left + rect.width / 2,
            y: rect.top,
        });
    }, [tooltip]);

    const handleMouseLeave = useCallback(() => setTip(null), []);

    return (
        <>
            <span
                className={`ob-badge ${taskStatusClass(status)}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children}
            </span>

            {tip && ReactDOM.createPortal(
                <div
                    className="ob-tooltip"
                    style={{ left: tip.x, top: tip.y }}
                >
                    {tip.text}
                </div>,
                document.body
            )}
        </>
    );
};

// Task names come back as "<Task description> - <Person Name>".
// Strip the trailing " - Person Name" so badges/tooltips read cleanly.
const stripPersonName = (taskName: string) => {
    const idx = taskName.lastIndexOf(" - ");
    return idx === -1 ? taskName : taskName.slice(0, idx).trim();
};

// Best-effort icon per task, matched by keyword against the cleaned task
// name. Falls back to a generic clipboard icon for anything unrecognized.
const getTaskIcon = (taskName: string): IconType => {
    const t = taskName.toLowerCase();

    if (t.includes("teams onboarding")) return PiMicrosoftTeamsLogo;
    if (t.includes("learn about") && t.includes("team")) return PiUsersThree;
    if (t.includes("learn about") && t.includes("product")) return PiPackage;
    if (t.includes("training")) return PiGraduationCap;
    if (t.includes("sprint") || t.includes("scrum")) return PiListChecks;
    if (t.includes("sign document")) return PiFileText;
    if (t.includes("welcome kit")) return PiGift;
    if (t.includes("receive access") || t.includes("email and notion")) return PiEnvelopeSimple;
    if (t.includes("send id") || t.includes("bank") || t.includes("emergency contact")) return PiIdentificationCard;
    if (t.includes("orientation") || t.includes("presentation")) return PiPresentation;
    if (t.includes("building access")) return PiBuildings;
    if (t.includes("clock in")) return PiClock;

    return PiClipboardText;
};

/* ─────────────────────────────────────────────────────────── */

interface OnboardingLeader {
    id: string;
    name: string;
    email: string;
}

interface OnboardingTask {
    id: string;
    task_name: string;
    status: string;
}

interface OnboardingUser {
    name: string;
    email: string | null;
    leader: OnboardingLeader[];
    tasks: OnboardingTask[];
}

// A locally-generated id, since `email` is often null and can't be used
// as a unique key/identifier on its own.
interface OnboardingUserWithId extends OnboardingUser {
    _id: string;
}

const isTaskDone = (task: OnboardingTask) => task.status === "Done";

const isFullyOnboarded = (user: OnboardingUser) =>
    user.tasks.length > 0 && user.tasks.every(isTaskDone);

const taskStatusIcon = (status: string) => {
    if (status === "Done") return <PiCheckCircleFill />;
    if (status === "In Progress") return <PiSpinnerGap />;
    return <PiCircleDashed />;
};

const taskStatusClass = (status: string) => {
    if (status === "Done") return "done";
    if (status === "In Progress") return "in-progress";
    return "not-started";
};

const Onboarding = () => {

    const [users, setUsers] = useState<OnboardingUserWithId[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [search, setSearch] = useState("")
    const [expandedUsers, setExpandedUsers] = useState<string[]>([]);

    // Notify modal
    const [notifyUser, setNotifyUser] = useState<OnboardingUserWithId | null>(null)
    const [selectedTasks, setSelectedTasks] = useState<string[]>([])
    const [sendingNotify, setSendingNotify] = useState(false)
    const [notifyError, setNotifyError] = useState<string | null>(null)
    const [notifySuccess, setNotifySuccess] = useState<string | null>(null)

    // CSRF
    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    useEffect(() => {
        const fetchOnboardingData = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await fetch(
                    '/api/notion/onboarding/d8bfd4421687487fbdc2e9570acce1f4/',
                    { credentials: "same-origin" }
                );
                const raw = await res.json();

                // The endpoint may return a bare array, a single record, or an
                // object wrapping the array under a key (e.g. results/data/users).
                // Normalize whatever shape comes back into OnboardingUser[].
                let data: OnboardingUser[];
                if (Array.isArray(raw)) {
                    data = raw;
                } else if (raw && Array.isArray(raw.results)) {
                    data = raw.results;
                } else if (raw && Array.isArray(raw.data)) {
                    data = raw.data;
                } else if (raw && Array.isArray(raw.users)) {
                    data = raw.users;
                } else if (raw && typeof raw === 'object') {
                    // Single onboarding record returned directly
                    data = [raw as OnboardingUser];
                } else {
                    data = [];
                }

                const withIds: OnboardingUserWithId[] = data
                    .filter(user => user.name !== "Unassigned")
                    .map((user, index) => ({
                        ...user,
                        _id: `${user.email ?? user.name}-${index}`,
                    }));

                setUsers(withIds);
            } catch (err) {
                console.error('Failed fetching onboarding data:', err);
                setError('Failed to load onboarding data.');
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };
        fetchOnboardingData();
    }, []);

    const toggleUser = (id: string) => {
        setExpandedUsers(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
        );
    };

    const openNotifyModal = (user: OnboardingUserWithId) => {
        if (!user.email) {
            toast.error(`No email on file for ${user.name}.`);
            return;
        }

        // Default to pre-selecting the tasks that aren't done yet --
        // that's usually exactly what you're sending the reminder for.
        const pendingTaskIds = user.tasks
            .filter(task => !isTaskDone(task))
            .map(task => task.id);

        setNotifyUser(user);
        setSelectedTasks(pendingTaskIds);
        setNotifyError(null);
        setNotifySuccess(null);
    };

    const closeNotifyModal = () => {
        setNotifyUser(null);
        setSelectedTasks([]);
        setNotifyError(null);
        setNotifySuccess(null);
        setSendingNotify(false);
    };

    const toggleNotifyTask = (taskId: string) => {
        setSelectedTasks(prev =>
            prev.includes(taskId)
                ? prev.filter(id => id !== taskId)
                : [...prev, taskId]
        );
    };

    const sendNotification = async () => {
        if (!notifyUser || !notifyUser.email || selectedTasks.length === 0) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            setSendingNotify(true);
            setNotifyError(null);
            setNotifySuccess(null);

            // Keep the same order the tasks appear in on the card, and send
            // clean, person-name-stripped labels so the email reads nicely.
            const orderedTasks = notifyUser.tasks
                .filter(task => selectedTasks.includes(task.id))
                .map(task => stripPersonName(task.task_name));

            const res = await fetch(
                '/api/onboarding/notify/',
                {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken
                    },
                    body: JSON.stringify({
                        email: notifyUser.email,
                        tasks: orderedTasks,
                    }),
                }
            );

            if (!res.ok) {
                const data = await res.json().catch(() => null);
                throw new Error(data?.error || "Failed to send reminder.");
            }

            setNotifySuccess(`Reminder sent to ${notifyUser.name}.`);
            toast.success(`Reminder sent to ${notifyUser.name}.`);
        } catch (err) {
            console.error('Failed to send onboarding reminder:', err);
            const message = err instanceof Error ? err.message : "Failed to send reminder.";
            setNotifyError(message);
            toast.error(message);
        } finally {
            setSendingNotify(false);
        }
    };

    const filteredUsers = useMemo(() => {
        const q = search.toLowerCase()
        return users.filter(user =>
            user.name.toLowerCase().includes(q) ||
            (user.email ?? "").toLowerCase().includes(q)
        )
    }, [search, users])

    const fullyOnboarded = users.filter(isFullyOnboarded).length

    return (
        <div className="customer-directory-body">

            <TeamHeader title="Human Resources" />
            <ToastContainer />

            <div className="support-container">

                <div className="support-page-header">
                    <div>
                        <h1 className="support-page-title">
                            Onboarding
                        </h1>
                        <p className="support-page-sub">
                            Track onboarding progress and outstanding tasks for new team members.
                        </p>
                    </div>
                </div>

                {/* Stats */}

                <div className="account-stats">

                    <div className="account-stat-card">
                        <PiUsers />
                        <span>{users.length}</span>
                        <small>Onboarding</small>
                    </div>

                    <div className="account-stat-card">
                        <PiClipboardText />
                        <span>{fullyOnboarded}/{users.length}</span>
                        <small>Fully Onboarded</small>
                    </div>

                </div>

                {/* Search */}

                <div className="account-toolbar">
                    <div className="account-search">
                        <PiMagnifyingGlass />
                        <input
                            placeholder="Search by name or email..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {loading && (
                    <p className="support-page-sub">Loading onboarding data...</p>
                )}

                {!loading && error && (
                    <p className="support-page-sub">{error}</p>
                )}

                {!loading && !error && (

                    <div className="account-user-list">

                        {filteredUsers.map(user => {

                            const expanded = expandedUsers.includes(user._id);
                            const onboarded = isFullyOnboarded(user);
                            const completedCount = user.tasks.filter(isTaskDone).length;
                            const totalCount = user.tasks.length;
                            const percent = totalCount > 0
                                ? Math.round((completedCount / totalCount) * 100)
                                : 0;
                            const leader = user.leader?.[0];

                            return (
                                <div
                                    key={user._id}
                                    className={`account-user-card ${expanded ? "expanded" : ""}`}
                                >

                                    <div
                                        className="account-user-header"
                                        onClick={() => toggleUser(user._id)}
                                    >

                                        <div className="account-user-left">

                                            <div className="account-avatar">
                                                <PiUserCircle />
                                            </div>

                                            <div className='account-user-left-name'>
                                                <div className="account-user-name-row">
                                                    <h3>{user.name}</h3>
                                                    <div className="onboarding-icon-badges">
                                                        {user.tasks.map(task => {
                                                            const Icon = getTaskIcon(task.task_name);
                                                            const cleanName = stripPersonName(task.task_name);
                                                            return (
                                                                <TaskBadge
                                                                    key={task.id}
                                                                    status={task.status}
                                                                    tooltip={`${cleanName} — ${task.status}`}
                                                                >
                                                                    <Icon />
                                                                </TaskBadge>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                <p>{user.email ?? "No email on file"}</p>
                                            </div>

                                        </div>

                                        <div className="account-user-summary">

                                            <div className={`onboarding-badge ${onboarded ? "complete" : "incomplete"}`}>
                                                {onboarded ? (
                                                    <><PiCheckCircleFill /> All Set</>
                                                ) : (
                                                    <><PiXCircleFill /> Incomplete</>
                                                )}
                                            </div>

                                            <div className="hours-pill">
                                                {completedCount}/{totalCount} tasks
                                            </div>

                                            {expanded ? (
                                                <PiCaretDown className="expand-icon" />
                                            ) : (
                                                <PiCaretRight className="expand-icon" />
                                            )}

                                        </div>

                                    </div>

                                    {expanded && (
                                        <>
                                            <div className="onboarding-section">

                                                <div className="onboarding-section-header">
                                                    <PiClipboardText />
                                                    <span>Task Progress</span>
                                                    <span className={`onboarding-section-status ${onboarded ? "complete" : "incomplete"}`}>
                                                        {percent}% complete
                                                    </span>
                                                </div>

                                                <div className="onboarding-progress-track">
                                                    <div
                                                        className="onboarding-progress-fill"
                                                        style={{ width: `${percent}%` }}
                                                    />
                                                </div>

                                                {leader && (
                                                    <div className="onboarding-leader-row">
                                                        <PiCrown />
                                                        <span>Leader: {leader.name}</span>
                                                        <span className="onboarding-leader-email">{leader.email}</span>
                                                    </div>
                                                )}

                                                <div className="onboarding-task-list">
                                                    {user.tasks.map(task => (
                                                        <div
                                                            key={task.id}
                                                            className={`onboarding-task-item ${taskStatusClass(task.status)}`}
                                                        >
                                                            <div className="onboarding-task-icon">
                                                                {taskStatusIcon(task.status)}
                                                            </div>
                                                            <span className="onboarding-task-name">
                                                                {task.task_name}
                                                            </span>
                                                            <span className="onboarding-task-status">
                                                                {task.status}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>

                                            </div>

                                            <div className="account-actions clockify-user-actions">
                                                <button
                                                    className="btn-ghost"
                                                    title={user.email ? undefined : "No email on file"}
                                                    onClick={() => openNotifyModal(user)}
                                                >
                                                    <PiBell />
                                                    Notify
                                                </button>
                                            </div>
                                        </>
                                    )}

                                </div>
                            );

                        })}

                    </div>

                )}

            </div>

            {/* Notify Modal */}

            {notifyUser && (
                <div
                    className="modal-overlay"
                    onClick={closeNotifyModal}
                >
                    <div
                        className="modal-box account-password-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <h3 className="modal-title">Send Onboarding Reminder</h3>
                            <button
                                className="modal-close-btn"
                                onClick={closeNotifyModal}
                            >
                                <PiX />
                            </button>
                        </div>

                        <div className="modal-body">

                            <p className="support-page-sub">
                                Choose which outstanding task(s) to remind <strong>{notifyUser.name}</strong> about.
                            </p>

                            <div className="notify-day-picker">
                                {notifyUser.tasks.map(task => {
                                    const active = selectedTasks.includes(task.id);
                                    const Icon = getTaskIcon(task.task_name);
                                    const cleanName = stripPersonName(task.task_name);
                                    return (
                                        <button
                                            key={task.id}
                                            type="button"
                                            className={`notify-day-chip ${active ? "active" : ""}`}
                                            onClick={() => toggleNotifyTask(task.id)}
                                        >
                                            <Icon />
                                            <span>{cleanName}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {notifyError && (
                                <p className="notify-feedback error">{notifyError}</p>
                            )}

                            {notifySuccess && (
                                <p className="notify-feedback success">{notifySuccess}</p>
                            )}

                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn-ghost"
                                onClick={closeNotifyModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={sendNotification}
                                disabled={selectedTasks.length === 0 || sendingNotify}
                            >
                                <PiBell />
                                {sendingNotify ? "Sending..." : "Send Reminder"}
                            </button>
                        </div>

                    </div>
                </div>
            )}

        </div>
    )
}

export default Onboarding